import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LOGO } from "@/data/exercises";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, CalendarDays, Sparkles, Quote, Loader2, Sun, MapPin, Music, ExternalLink, Camera, ChevronLeft, ChevronRight } from "lucide-react";

export default function Ephemeride() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ville, setVille] = useState(localStorage.getItem("eph_ville") || "Paris");
  const [input, setInput] = useState(localStorage.getItem("eph_ville") || "Paris");
  const [photoIdx, setPhotoIdx] = useState(0);

  const load = useCallback((v) => {
    setLoading(true);
    setPhotoIdx(0);
    api.get("/ephemeride", { params: { ville: v || "Paris" } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(ville); }, [load, ville]);

  const applyCity = () => {
    const v = input.trim() || "Paris";
    localStorage.setItem("eph_ville", v);
    setVille(v);
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-care">
      <header className="bg-white/70 border-b border-amber-100 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Passerelle" className="h-10 w-auto" />
            <span className="text-gray-300">·</span>
            <span className="text-gray-500 text-sm font-semibold">Éphéméride</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="ephemeride-back-button">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-10 h-10 animate-spin text-amber-500" /></div>
        ) : !data ? (
          <p className="text-center text-gray-400 py-20">Éphéméride indisponible pour le moment.</p>
        ) : (
          <div className="soft-in" data-testid="ephemeride-content">
            {/* Date + météo */}
            <div className="rounded-[2rem] overflow-hidden border-4 border-amber-200 bg-gradient-to-br from-amber-50 via-white to-emerald-50 p-8 sm:p-10 text-center mb-8">
              <div className="inline-flex items-center gap-2 text-amber-600 font-bold mb-3">
                <CalendarDays className="w-5 h-5" /> Aujourd'hui
              </div>
              <h1 className="font-serif-display text-4xl sm:text-5xl font-normal text-gray-800 mb-6" data-testid="ephemeride-date">
                {data.date_label}
              </h1>
              {data.temperature != null && (
                <div className="inline-flex items-center gap-3 rounded-full bg-white border-4 border-emerald-100 px-6 py-3" data-testid="ephemeride-temp">
                  <Sun className="w-8 h-8 text-amber-400" />
                  <span className="text-3xl font-patient font-black text-gray-800">{Math.round(data.temperature)}°C</span>
                  <span className="text-gray-400 font-patient">à {data.ville}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 justify-center mb-8" data-testid="ephemeride-city">
              <MapPin className="w-5 h-5 text-emerald-600" />
              <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyCity()}
                placeholder="Ville du résident" className="max-w-xs h-12" data-testid="ephemeride-city-input" />
              <Button onClick={applyCity} className="h-12 bg-emerald-700 hover:bg-emerald-800" data-testid="ephemeride-city-button">Voir</Button>
            </div>

            {/* Saint du jour */}
            <div className="rounded-3xl bg-white border-4 border-emerald-700/10 p-8 mb-8" data-testid="ephemeride-saint">
              <div className="flex items-center gap-2 text-emerald-700 font-patient font-bold text-xl mb-3">
                <Sparkles className="w-6 h-6" /> Le saint du jour
              </div>
              <h2 className="font-serif-display text-3xl font-normal text-gray-800 mb-3">{data.saint || "—"}</h2>
              {data.histoire && <p className="text-lg font-patient text-gray-600 leading-relaxed">{data.histoire}</p>}
            </div>

            {(data.souvenir || data.chanson) && (() => {
              const photos = (data.photos && data.photos.length ? data.photos : (data.photo_url ? [data.photo_url] : []));
              const idx = photos.length ? photoIdx % photos.length : 0;
              return (
              <div className="rounded-3xl bg-amber-50 border-4 border-amber-200 p-8 mb-8" data-testid="ephemeride-souvenir">
                <div className="flex items-center gap-2 text-amber-700 font-patient font-bold text-xl mb-3">
                  <Camera className="w-6 h-6" /> Souvenir du jour
                </div>
                {photos.length > 0 && (
                  <div className="relative mb-4" data-testid="ephemeride-photo-carousel">
                    <img src={photos[idx]} alt="Souvenir d'époque" loading="lazy"
                      className="w-full h-56 object-cover rounded-2xl border-2 border-amber-200"
                      data-testid="ephemeride-photo"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    {photos.length > 1 && (
                      <>
                        <button type="button" aria-label="Photo précédente"
                          onClick={() => setPhotoIdx((i) => (i - 1 + photos.length) % photos.length)}
                          data-testid="ephemeride-photo-prev"
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 border-2 border-amber-300 text-amber-800 flex items-center justify-center shadow-md hover:bg-white transition-colors">
                          <ChevronLeft className="w-7 h-7" />
                        </button>
                        <button type="button" aria-label="Photo suivante"
                          onClick={() => setPhotoIdx((i) => (i + 1) % photos.length)}
                          data-testid="ephemeride-photo-next"
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 border-2 border-amber-300 text-amber-800 flex items-center justify-center shadow-md hover:bg-white transition-colors">
                          <ChevronRight className="w-7 h-7" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2" data-testid="ephemeride-photo-dots">
                          {photos.map((_, i) => (
                            <button key={i} type="button" aria-label={`Photo ${i + 1}`}
                              onClick={() => setPhotoIdx(i)}
                              className={`w-3 h-3 rounded-full transition-colors ${i === idx ? "bg-amber-500" : "bg-white/70 border border-amber-300"}`} />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
                {data.photo_local && photos.length > 0 && (
                  <p className="text-sm text-amber-700 font-semibold mb-3 -mt-2" data-testid="ephemeride-photo-local">
                    Photos en lien avec {data.ville}
                  </p>
                )}
                {data.souvenir && <p className="text-lg font-patient text-gray-700 leading-relaxed mb-4">{data.souvenir}</p>}
                {data.chanson && (
                  <a href={`https://www.youtube.com/results?search_query=${encodeURIComponent(data.chanson)}`}
                    target="_blank" rel="noreferrer" data-testid="ephemeride-chanson"
                    className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-white border-2 border-amber-300 text-amber-800 font-patient font-bold">
                    <Music className="w-5 h-5" /> {data.chanson} <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
              );
            })()}

            {/* Dicton */}
            {data.dicton && (
              <div className="rounded-3xl bg-emerald-700 text-white p-8 relative overflow-hidden" data-testid="ephemeride-dicton">
                <Quote className="w-10 h-10 text-white/30 mb-2" />
                <p className="text-2xl font-patient font-bold italic leading-relaxed">« {data.dicton} »</p>
                <p className="mt-3 text-emerald-100 font-patient">Dicton du jour</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
